import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Store, Users, Package, Search, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

const ShopCard = ({ s }) => (
  <Link
    to={`/shop/${s.slug || s.user_id}`}
    data-testid={`shop-card-${s.user_id}`}
    className="group block rounded-xl overflow-hidden bg-white border border-black/10 hover:border-[#C05A3C]/50 hover:shadow-md transition-all"
  >
    <div className="h-24 bg-gradient-to-br from-[#C05A3C]/20 via-[#E8C07D]/20 to-[#2A3D36]/10 relative overflow-hidden">
      {s.banner_url && (
        <img src={s.banner_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      )}
    </div>
    <div className="px-4 pb-4">
      <div className="flex items-end gap-3 -mt-7">
        <div className="w-14 h-14 rounded-xl border-2 border-white bg-[#1A2024] overflow-hidden flex items-center justify-center shrink-0">
          {s.logo_url ? (
            <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-display text-xl">{(s.name || "S").slice(0, 1)}</span>
          )}
        </div>
        <div className="pb-1 min-w-0">
          <div className="font-semibold text-sm truncate">{s.name}</div>
          {s.location && (
            <div className="text-[11px] text-muted-foreground flex items-center gap-0.5">
              <MapPin className="w-3 h-3" /> {s.location}
            </div>
          )}
        </div>
      </div>
      {s.tagline && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{s.tagline}</p>}
      <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" /> {s.listing_count} items</span>
        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {s.follower_count} followers</span>
      </div>
    </div>
  </Link>
);

const Shops = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get(`/shops?limit=60${q ? `&q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setItems(r.data?.items || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="shops-page">
      <section className="bg-[#1A2024] text-white">
        <div className="container-narrow py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-semibold text-[#E8C07D] mb-3">
            <Store className="w-3.5 h-3.5" /> Seller Shops
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight max-w-2xl">
            Discover shops from the African diaspora.
          </h1>
          <p className="text-white/70 mt-4 max-w-xl font-sans-ui">
            Browse independent sellers, makers and brands — each with their own storefront
            of products and services. Find a shop you love and follow it.
          </p>
          <div className="mt-6 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search shops…"
              data-testid="shops-search"
              className="pl-9 bg-white text-black h-11"
            />
          </div>
        </div>
      </section>

      <div className="container-narrow py-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="shops-empty">
            <Store className="w-12 h-12 mx-auto text-black/15 mb-3" />
            {q ? "No shops match your search." : "No shops yet — be the first to open one!"}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shops-grid">
            {items.map((s) => <ShopCard key={s.user_id} s={s} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Shops;
